import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import {
  registerTokenProvider,
  registerTokenRefresher,
  registerUnauthenticatedHandler,
} from '@/lib/api-client';
import {
  getStoredRefreshToken,
  loginApi,
  logoutApi,
  refreshApi,
  removeRefreshToken,
  storeRefreshToken,
} from '@/lib/auth-api';
import type { AuthUser } from '@/stores/auth.store';
import { clearPersistedUser, persistUser, restoreUser, useAuthStore } from '@/stores/auth.store';

registerTokenProvider(() => useAuthStore.getState().accessToken);

registerTokenRefresher(async () => {
  const rt = getStoredRefreshToken();
  if (!rt) return null;
  const controller = new AbortController();
  const tid = setTimeout(() => {
    controller.abort();
  }, 10_000);
  try {
    const tokens = await refreshApi(rt, controller.signal);
    const restored = restoreUser();
    if (restored) {
      useAuthStore.getState().setAuth(restored.user, tokens.accessToken, restored.tenantSlug);
    }
    storeRefreshToken(tokens.refreshToken);
    return tokens.accessToken;
  } catch {
    return null;
  } finally {
    clearTimeout(tid);
  }
});

registerUnauthenticatedHandler(() => {
  removeRefreshToken();
  clearPersistedUser();
  useAuthStore.getState().clearAuth();
});

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login(email: string, password: string, tenantSlug: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { user, isAuthenticated, setAuth, clearAuth } = useAuthStore();

  useEffect(() => {
    const storedRefresh = getStoredRefreshToken();
    const restored = restoreUser();

    if (!storedRefresh || !restored) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    // 10 s timeout — Render cold starts can take ~40 s. We'd rather show the
    // login page fast and let the user submit while the server warms up than
    // show a spinner indefinitely.
    const tid = setTimeout(() => {
      controller.abort();
    }, 10_000);

    refreshApi(storedRefresh, controller.signal)
      .then((tokens) => {
        setAuth(restored.user, tokens.accessToken, restored.tenantSlug);
        storeRefreshToken(tokens.refreshToken);
      })
      .catch(() => {
        removeRefreshToken();
        clearPersistedUser();
        clearAuth();
      })
      .finally(() => {
        clearTimeout(tid);
        setIsLoading(false);
      });

    return () => {
      controller.abort();
      clearTimeout(tid);
    };
  }, [setAuth, clearAuth]);

  const login = useCallback(
    async (email: string, password: string, tenantSlug: string): Promise<void> => {
      const result = await loginApi(email, password, tenantSlug);
      if (result.mfaRequired) {
        throw new Error('MFA required — not yet supported in this flow');
      }
      // Clear stale cache from any previous user session before setting new identity.
      queryClient.clear();
      setAuth(result.user, result.tokens.accessToken, tenantSlug);
      persistUser(result.user, tenantSlug);
      storeRefreshToken(result.tokens.refreshToken);
    },
    [setAuth, queryClient],
  );

  const logout = useCallback(async (): Promise<void> => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      await logoutApi(accessToken).catch(() => undefined);
    }
    removeRefreshToken();
    clearPersistedUser();
    clearAuth();
  }, [clearAuth]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
